/**
 * Accessible tabbed content.
 *
 * Markup:
 *   <tabbed-content>
 *     <div role="tablist">...</div>
 *     <div role="tabpanel" id="panel-1">...</div>
 *   </tabbed-content>
 *
 * Tabs: [role="tab"][aria-controls="panel-id"]
 * Panels stay mounted; slideshow-list is refreshed (not destroyed) on show.
 */

if (!customElements.get('tabbed-content')) {
  class TabbedContent extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      if (!this.tabs.length || !this.panels.length) return;

      this.onTabClick = (event) => this.selectTab(event.currentTarget, { focus: false });
      this.onTabKeydown = this.onTabKeydown.bind(this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeydown);
      });

      const selected =
        this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.selectTab(selected, { focus: false, silent: true });
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });
    }

    onTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      const last = this.tabs.length - 1;
      let nextIndex = currentIndex;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = currentIndex === last ? 0 : currentIndex + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = currentIndex === 0 ? last : currentIndex - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.selectTab(this.tabs[nextIndex], { focus: true });
    }

    selectTab(tab, { focus = false, silent = false } = {}) {
      if (!tab) return;

      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;

      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
        item.classList.toggle('is-active', selected);
      });

      this.panels.forEach((item) => {
        const selected = item === panel;
        item.hidden = !selected;
        item.classList.toggle('is-active', selected);
      });

      if (focus) tab.focus();

      if (panel && !silent) {
        // Wait for layout after removing [hidden], then refresh carousels.
        requestAnimationFrame(() => this.refreshPanelCarousels(panel));
      }

      this.dispatchEvent(
        new CustomEvent('tabbed-content:change', {
          bubbles: true,
          detail: { tab, panel },
        })
      );
    }

    refreshPanelCarousels(panel) {
      panel.querySelectorAll('slideshow-list').forEach((list) => {
        if (typeof list.refresh === 'function') {
          list.refresh();
          return;
        }

        // Fallback for older slideshow-list builds.
        if (list.swiper && !list.swiper.destroyed) {
          list.swiper.update();
          return;
        }

        list.initSwiper?.();
      });
    }
  }

  customElements.define('tabbed-content', TabbedContent);
}
